import { useState } from "react";
import { factoryProducts, factoryCategories } from "@/mocks/factory";

interface Product {
  id: number;
  sellerName: string;
  sellerAvatar: string;
  country: string;
  flag: string;
  verified: boolean;
  category: string;
  productName: string;
  description: string;
  images: string[];
  priceList: { qty: string; price: string }[];
  minOrder: string;
  leadTime: string;
  whatsapp: string;
  email: string;
  rating: number;
  reviews: number;
  tags: string[];
}

function StarRating({ rating }: { rating: number }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => {
        let cls = "ri-star-line";
        if (s <= Math.floor(rating)) cls = "ri-star-fill";
        else if (s - 0.5 <= rating) cls = "ri-star-half-fill";
        return <i key={s} className={`${cls} text-amber-400 text-xs`}></i>;
      })}
    </span>
  );
}

function ProductCard({ product, onOrder, onContact }: { product: Product; onOrder: (p: Product) => void; onContact: (p: Product) => void }) {
  const [imgIdx, setImgIdx] = useState(0);

  return (
    <div className="bg-white rounded-xl overflow-hidden border border-stone-100 hover:border-amber-200 transition-all duration-200 group flex flex-col">
      {/* Image */}
      <div className="relative h-48 overflow-hidden bg-stone-50">
        <img
          src={product.images[imgIdx]}
          alt={product.productName}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        {product.images.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
            {product.images.map((_, i) => (
              <button
                key={i}
                onClick={() => setImgIdx(i)}
                className={`w-1.5 h-1.5 rounded-full transition-colors cursor-pointer ${i === imgIdx ? "bg-amber-500" : "bg-white/70"}`}
              />
            ))}
          </div>
        )}
        {product.verified && (
          <span className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
            <i className="ri-shield-check-fill text-xs"></i> Verified
          </span>
        )}
        <span className="absolute top-2 right-2 bg-white/90 text-stone-700 text-xs font-medium px-2 py-0.5 rounded-full">
          {product.flag} {product.country}
        </span>
      </div>

      {/* Content */}
      <div className="p-4 flex flex-col flex-1">
        {/* Seller */}
        <div className="flex items-center gap-2 mb-3">
          <img src={product.sellerAvatar} alt={product.sellerName} className="w-8 h-8 rounded-full object-cover object-top flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-semibold text-gray-800 truncate">{product.sellerName}</p>
            <span className="text-xs text-amber-600">{product.category}</span>
          </div>
        </div>

        <h3 className="font-bold text-gray-900 text-sm mb-1.5 line-clamp-2">{product.productName}</h3>
        <p className="text-stone-500 text-xs leading-relaxed mb-3 line-clamp-2">{product.description}</p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1 mb-3">
          {product.tags.map((tag) => (
            <span key={tag} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-100">{tag}</span>
          ))}
        </div>

        {/* Price List */}
        <div className="bg-stone-50 rounded-lg p-2.5 mb-3">
          <p className="text-xs font-semibold text-stone-600 mb-1.5">Price List</p>
          <div className="space-y-1">
            {product.priceList.map((row) => (
              <div key={row.qty} className="flex justify-between text-xs">
                <span className="text-stone-500">{row.qty}</span>
                <span className="font-bold text-green-700">{row.price}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Meta */}
        <div className="flex items-center gap-3 text-xs text-stone-400 mb-3">
          <span className="flex items-center gap-1"><i className="ri-box-3-line"></i>MOQ: {product.minOrder}</span>
          <span className="flex items-center gap-1"><i className="ri-time-line"></i>{product.leadTime}</span>
        </div>

        {/* Rating */}
        <div className="flex items-center gap-1.5 mb-4">
          <StarRating rating={product.rating} />
          <span className="text-xs font-semibold text-gray-700">{product.rating}</span>
          <span className="text-xs text-stone-400">({product.reviews} reviews)</span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 mt-auto">
          <button
            onClick={() => onOrder(product)}
            className="flex-1 text-xs font-bold py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-shopping-cart-line mr-1"></i>Place Order
          </button>
          <button
            onClick={() => onContact(product)}
            className="flex-1 text-xs font-bold py-2.5 rounded-lg border border-stone-200 text-stone-700 hover:border-amber-400 hover:text-amber-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-message-3-line mr-1"></i>Contact
          </button>
        </div>
      </div>
    </div>
  );
}

function OrderModal({ product, onClose }: { product: Product; onClose: () => void }) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [qty, setQty] = useState("");
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const notes = (form.querySelector("textarea[name='notes']") as HTMLTextAreaElement)?.value || "";
    if (notes.length > 500) return;
    setSubmitting(true);
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((val, key) => body.append(key, val.toString()));
    try {
      await fetch("https://readdy.ai/api/form/d7ciejnsch5bvrijf420", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-stone-100">
          <div>
            <h3 className="font-bold text-gray-900 text-base">Place Order Request</h3>
            <p className="text-xs text-stone-500 mt-0.5">{product.sellerName}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer text-stone-500">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {submitted ? (
          <div className="p-8 text-center">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
              <i className="ri-check-line text-3xl text-green-600"></i>
            </div>
            <h4 className="font-bold text-gray-900 text-lg mb-2">Order Request Sent!</h4>
            <p className="text-stone-500 text-sm mb-1">The seller will contact you within 24 hours.</p>
            <p className="text-stone-400 text-xs">Check your email for confirmation.</p>
            <button onClick={onClose} className="mt-6 px-6 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap">
              Done
            </button>
          </div>
        ) : (
          <form data-readdy-form onSubmit={handleSubmit} className="p-5 space-y-4">
            <input type="hidden" name="product" value={product.productName} />
            <input type="hidden" name="seller" value={product.sellerName} />

            <div className="bg-amber-50 rounded-lg p-3 flex gap-3 items-start">
              <img src={product.images[0]} alt="" className="w-14 h-14 rounded-lg object-cover object-top flex-shrink-0" />
              <div>
                <p className="text-xs font-bold text-gray-900">{product.productName}</p>
                <p className="text-xs text-stone-500 mt-0.5">MOQ: {product.minOrder} · Lead: {product.leadTime}</p>
                <p className="text-xs text-green-700 font-semibold mt-1">{product.priceList[0].price} (starting)</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-stone-600 block mb-1">Your Name *</label>
                <input name="buyer_name" required className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" placeholder="Full name" />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 block mb-1">Company</label>
                <input name="company" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" placeholder="Company name" />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 block mb-1">Email Address *</label>
              <input name="email" type="email" required className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" placeholder="your@email.com" />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-stone-600 block mb-1">WhatsApp / Phone</label>
                <input name="whatsapp" className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" placeholder="+1 234 567 8900" />
              </div>
              <div>
                <label className="text-xs font-semibold text-stone-600 block mb-1">Order Quantity *</label>
                <input
                  name="quantity"
                  required
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400"
                  placeholder={`Min: ${product.minOrder}`}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 block mb-1">Destination Country *</label>
              <input name="destination" required className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400" placeholder="e.g. United States" />
            </div>

            <div>
              <label className="text-xs font-semibold text-stone-600 block mb-1">
                Special Requirements / Notes
                <span className={`ml-2 font-normal ${charCount > 500 ? "text-red-500" : "text-stone-400"}`}>({charCount}/500)</span>
              </label>
              <textarea
                name="notes"
                rows={3}
                maxLength={500}
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full border border-stone-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-amber-400 resize-none"
                placeholder="Custom colors, logo, packaging requirements..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting || charCount > 500}
              className="w-full py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              {submitting ? "Sending..." : "Send Order Request"}
            </button>
            <p className="text-xs text-stone-400 text-center">The seller will respond within 24 hours via email or WhatsApp.</p>
          </form>
        )}
      </div>
    </div>
  );
}

function ContactModal({ product, onClose }: { product: Product; onClose: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);

  const copy = (text: string, type: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    });
  };

  const openWhatsApp = () => {
    const num = product.whatsapp.replace(/\D/g, "");
    window.open(`https://wa.me/${num}?text=Hi, I found your product "${product.productName}" on CrownBoostAdsJob.com and I'm interested in placing an order.`, "_blank");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-stone-100">
          <h3 className="font-bold text-gray-900 text-base">Contact Seller</h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 cursor-pointer text-stone-500">
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="p-5">
          {/* Seller Info */}
          <div className="flex items-center gap-3 mb-5 p-3 bg-stone-50 rounded-xl">
            <img src={product.sellerAvatar} alt={product.sellerName} className="w-12 h-12 rounded-full object-cover object-top flex-shrink-0" />
            <div>
              <p className="font-bold text-gray-900 text-sm">{product.sellerName}</p>
              <p className="text-xs text-stone-500">{product.flag} {product.country} · {product.category}</p>
              <div className="flex items-center gap-1 mt-0.5">
                <StarRating rating={product.rating} />
                <span className="text-xs text-stone-500">{product.rating} ({product.reviews})</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-stone-500 mb-4 text-center">Choose how you'd like to connect with this seller:</p>

          {/* WhatsApp */}
          <div className="border border-green-200 rounded-xl p-4 mb-3 bg-green-50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-green-500 rounded-full">
                  <i className="ri-whatsapp-line text-white text-base"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-900">WhatsApp</p>
                  <p className="text-xs text-stone-500">{product.whatsapp}</p>
                </div>
              </div>
              <button
                onClick={() => copy(product.whatsapp, "whatsapp")}
                className="text-xs text-stone-500 hover:text-green-600 cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                <i className={`${copied === "whatsapp" ? "ri-check-line text-green-600" : "ri-file-copy-line"}`}></i>
                {copied === "whatsapp" ? "Copied!" : "Copy"}
              </button>
            </div>
            <button
              onClick={openWhatsApp}
              className="w-full py-2.5 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-whatsapp-line"></i>
              Chat on WhatsApp
            </button>
          </div>

          {/* Email */}
          <div className="border border-amber-200 rounded-xl p-4 bg-amber-50">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-500 rounded-full">
                  <i className="ri-mail-line text-white text-base"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-900">Email</p>
                  <p className="text-xs text-stone-500">{product.email}</p>
                </div>
              </div>
              <button
                onClick={() => copy(product.email, "email")}
                className="text-xs text-stone-500 hover:text-amber-600 cursor-pointer flex items-center gap-1 whitespace-nowrap"
              >
                <i className={`${copied === "email" ? "ri-check-line text-amber-600" : "ri-file-copy-line"}`}></i>
                {copied === "email" ? "Copied!" : "Copy"}
              </button>
            </div>
            <a
              href={`mailto:${product.email}?subject=Inquiry: ${product.productName}&body=Hi, I found your product on CrownBoostAdsJob.com and I'm interested in placing an order.`}
              className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
            >
              <i className="ri-mail-send-line"></i>
              Send Email
            </a>
          </div>

          <p className="text-xs text-stone-400 text-center mt-4">
            <i className="ri-shield-check-line text-green-500 mr-1"></i>
            Seller verified by CrownBoost. Always use secure payment methods.
          </p>
        </div>
      </div>
    </div>
  );
}

export default function GlobalFactorySection() {
  const [activeCategory, setActiveCategory] = useState("All");
  const [orderProduct, setOrderProduct] = useState<Product | null>(null);
  const [contactProduct, setContactProduct] = useState<Product | null>(null);
  const [showAll, setShowAll] = useState(false);

  const filtered = activeCategory === "All"
    ? factoryProducts
    : factoryProducts.filter((p) => p.category === activeCategory);

  const displayed = showAll ? filtered : filtered.slice(0, 6);

  return (
    <section id="global-factory" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <span className="text-amber-500 text-sm font-semibold uppercase tracking-wider">Global Trade</span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-1">Global Factory &amp; Seller Marketplace</h2>
            <p className="text-stone-500 text-sm mt-1.5">Connect directly with verified factories worldwide — browse products, get price lists, place orders &amp; chat via WhatsApp or Email.</p>
          </div>
          <button className="text-sm font-semibold px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-white transition-colors cursor-pointer whitespace-nowrap w-fit flex items-center gap-2">
            <i className="ri-store-2-line"></i>
            List Your Factory / Products
          </button>
        </div>

        {/* Stats Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { icon: "ri-global-line", label: "Countries", value: "50+" },
            { icon: "ri-store-2-line", label: "Verified Sellers", value: "2,400+" },
            { icon: "ri-box-3-line", label: "Products Listed", value: "18,000+" },
            { icon: "ri-shield-check-line", label: "Orders Completed", value: "95,000+" },
          ].map((stat) => (
            <div key={stat.label} className="bg-amber-50 rounded-xl p-3 flex items-center gap-3">
              <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                <i className={`${stat.icon} text-amber-600 text-base`}></i>
              </div>
              <div>
                <p className="font-bold text-gray-900 text-sm">{stat.value}</p>
                <p className="text-xs text-stone-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {factoryCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setShowAll(false); }}
              className={`text-xs font-medium px-4 py-2 rounded-full transition-colors cursor-pointer whitespace-nowrap ${
                activeCategory === cat
                  ? "bg-amber-500 text-white"
                  : "bg-stone-100 text-stone-600 hover:bg-amber-100 hover:text-amber-700"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {displayed.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onOrder={setOrderProduct}
              onContact={setContactProduct}
            />
          ))}
        </div>

        {/* Load More */}
        {filtered.length > 6 && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAll(!showAll)}
              className="px-8 py-3 border-2 border-amber-400 text-amber-600 font-semibold rounded-lg hover:bg-amber-500 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              {showAll ? "Show Less" : `View All ${filtered.length} Products`}
            </button>
          </div>
        )}

        {/* CTA Banner */}
        <div className="mt-10 rounded-2xl overflow-hidden relative">
          <img
            src="https://public.readdy.ai/ai/img_res/92b5ef451cd24441c0a2c3ef4864eda2.jpg"
            alt="Join as a seller"
            className="w-full h-44 object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent flex items-center px-8">
            <div>
              <h3 className="text-white font-bold text-xl md:text-2xl mb-1">Are You a Factory or Seller?</h3>
              <p className="text-white/80 text-sm mb-4">Upload your products, set your price list, and connect with global buyers today.</p>
              <div className="flex flex-wrap gap-3">
                <button className="px-5 py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-bold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-upload-cloud-line mr-1.5"></i>Upload Products
                </button>
                <button className="px-5 py-2.5 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap border border-white/40">
                  Learn More
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {orderProduct && <OrderModal product={orderProduct} onClose={() => setOrderProduct(null)} />}
      {contactProduct && <ContactModal product={contactProduct} onClose={() => setContactProduct(null)} />}
    </section>
  );
}
